/**
 * Chinese Classical match structure — dealer seat, round, hand-within-round
 * tracking, and per-seat running scores. Pure data, no canvas/DOM/timers,
 * matching the `game/`/`ai/`/`coach/` convention.
 *
 * This module only provides the STATE SHAPE plus a constructor and pure
 * predicates. Turning a completed hand's `HandResult` into the next
 * `MatchState` (dealer-repeat-vs-rotate decision, score payment) is
 * `advanceMatch`'s job (T14), not this file's.
 *
 * Dealer rotation rule this shape supports: a round starts with seat 0 as
 * dealer (`dealerSeatsSeen: 1`). The dealer REPEATS (same seat, count
 * unchanged) when the dealer wins or the hand is drawn/exhausted. The dealer
 * ROTATES to the next seat (count increments) only when a non-dealer wins.
 * Once every seat has dealt in a round (`dealerSeatsSeen === SEAT_COUNT`),
 * the NEXT rotation flips the round (`east` -> `south`, count resets to 1)
 * instead of just incrementing. The match ends once a non-dealer win would
 * rotate the dealer past the `south` round's 4th dealer (a 5th rotation).
 */

import { SEAT_COUNT, type Seat } from '../game/types.js';
import { nextSeat as coreNextSeat } from '../game/stateCore.js';

export type Round = 'east' | 'south';

export interface MatchState {
  readonly round: Round;
  readonly dealer: Seat;
  /** 1..SEAT_COUNT — distinct seats that have held dealer this round so far. */
  readonly dealerSeatsSeen: number;
  readonly scores: Readonly<Record<Seat, number>>;
  readonly finished: boolean;
}

/** Starting per-seat score for a new match — see plan doc's open "starting points" question. */
export const STARTING_POINTS = 2000;

const INITIAL_DEALER: Seat = 0;
const INITIAL_ROUND: Round = 'east';
const INITIAL_DEALER_SEATS_SEEN = 1;

export function createMatch(startingPoints: number = STARTING_POINTS): MatchState {
  return {
    round: INITIAL_ROUND,
    dealer: INITIAL_DEALER,
    dealerSeatsSeen: INITIAL_DEALER_SEATS_SEEN,
    scores: { 0: startingPoints, 1: startingPoints, 2: startingPoints, 3: startingPoints },
    finished: false,
  };
}

export const nextSeat: (seat: Seat) => Seat = coreNextSeat;

/** True once every seat has held dealer this round. */
export function isRoundComplete(matchState: MatchState): boolean {
  return matchState.dealerSeatsSeen === SEAT_COUNT;
}

/** True once the south round has run every seat through the dealer role. */
export function isMatchComplete(matchState: MatchState): boolean {
  return matchState.round === 'south' && isRoundComplete(matchState);
}
